// @flow

import * as React from 'react';

import { localize } from '@ta/common.i18n';
import { SocialLabel, SocialTab, SocialTabs } from '@ta/social.tabs';
import { featureIsEnabled } from '@ta/common.features';
import { InfiniteLoader } from '@ta/common.infinite-loader';
import { DeprecatedRoutedModal } from '@ta/overlays.modal';
import { TitleBar } from '@ta/modal.headers';
import { FollowTabTypeContext } from '@ta/tracking.interactions';
import type {
  SuggestedFolloweesTypeInput,
  SuggestedFolloweesPlacementInput,
} from '@ta/social.member-types';

import SuggestedFacebookMembers from './SuggestedFacebookMembers';
import SuggestedFacebookMembersDataProvider from './SuggestedFacebookMembersDataProvider';
// eslint-disable-next-line import/no-cycle
import SuggestedTripAdvisorMembersSections from './SuggestedTripAdvisorMembersSections';
import ValidatedFacebookToken from './ValidatedFacebookToken';

import styles from './SuggestedMembersModal.less';

type Props = {|
  /** The connectionTypes */
  type: 'TripAdvisor' | 'Facebook',
  /** A modal name */
  modalName: string,
  /** modal titel */
  title: string,
  /** The followee types to show in the 'Suggested for you' tab */
  followeesTypes?: SuggestedFolloweesTypeInput[],
  /** the placement on the site where the suggested followees will appear */
  placement?: SuggestedFolloweesPlacementInput,
  /** callback for closing the modal */
  onClose?: () => void,
  /** show facebook tab */
  showFacebookTab?: boolean,
|};

const module = 'Social_suggested_fb_members';
const INFINITE_LOADER_THRESHOLD = 550;
const INFINITE_LOADER_THROTTLE = 50;

const SuggestedMembersModal = (props: Props) => {
  const { type, title, modalName, followeesTypes, onClose, showFacebookTab } = props;

  const tabs = () => {
    const tabsToRender: Array<React.Element<typeof SocialTab>> = [];

    const suggestedLabel = localize('social_label_Suggested_for_you'); // Suggested for you
    tabsToRender.push(
      <SocialTab
        key={`${type}_tab_${Math.random()}`}
        isActive={type === 'TripAdvisor'}
        label={<SocialLabel text={suggestedLabel} />}
        socialLabelText={suggestedLabel}
      >
        <div className={styles.suggestions}>
          <FollowTabTypeContext.Provider value={{ tabType: 'SUGGESTED_FOR_YOU' }}>
            <SuggestedTripAdvisorMembersSections limit={20} prioritizedTypes={followeesTypes} />
          </FollowTabTypeContext.Provider>
        </div>
      </SocialTab>,
    );

    if (featureIsEnabled('suggested_facebook_connections') && showFacebookTab) {
      const facebookLabel = localize('common_Facebook_friends'); // Facebook friends
      tabsToRender.push(
        <SocialTab
          key={`${type}_tab_${Math.random()}`}
          isActive={type === 'Facebook'}
          label={<SocialLabel icon="facebook" text={facebookLabel} />}
          socialLabelText={facebookLabel}
          trackingProps={{
            module,
            action: 'facebook_friends_tab_shown',
          }}
        >
          <SuggestedFacebookMembersDataProvider limit={20}>
            {(
              updateRecommendedConnections,
              recommendedConnections,
              hasMore,
              connectionStatus,
              loading,
              error,
              refetch,
            ) => (
              <InfiniteLoader
                throttle={INFINITE_LOADER_THROTTLE}
                threshold={INFINITE_LOADER_THRESHOLD}
                hasMore={hasMore}
                loadMore={updateRecommendedConnections}
                infiniteLoadOnMount
              >
                {(containerRef, contentRef) => (
                  <div className={styles.suggestions} ref={containerRef}>
                    <FollowTabTypeContext.Provider value={{ tabType: 'FACEBOOK_FRIENDS' }}>
                      <ValidatedFacebookToken trackingModule={module}>
                        <SuggestedFacebookMembers
                          connections={recommendedConnections}
                          contentRef={contentRef}
                          loading={loading}
                          error={error}
                          refetch={refetch}
                          status={connectionStatus}
                        />
                      </ValidatedFacebookToken>
                    </FollowTabTypeContext.Provider>
                  </div>
                )}
              </InfiniteLoader>
            )}
          </SuggestedFacebookMembersDataProvider>
        </SocialTab>,
      );
    }

    return <SocialTabs flexPanel>{tabsToRender}</SocialTabs>;
  };

  return (
    <DeprecatedRoutedModal
      name={modalName}
      isFullBleed
      bodyScroll={false}
      onClose={onClose}
      header={<TitleBar displayType="LEFT_ALIGN_GRAY">{title}</TitleBar>}
    >
      <div className={styles.suggested_list}>{tabs()}</div>
    </DeprecatedRoutedModal>
  );
};

SuggestedMembersModal.defaultProps = {
  /** Discover travel ideas + advice from these trusted experts */
  title: localize('Follow_modal_update_1217'),
  followeesTypes: undefined,
  placement: undefined,
  onClose: () => {},
  showFacebookTab: true,
};

export default SuggestedMembersModal;
